import { useDebounceFn } from '@vueuse/core'

export default function useCarousel(carouselRef: Ref<HTMLElement | null>, cardSelector: string = '.card') {
	const showLeftArrow = ref(false)
	const showRightArrow = ref(false)
	const currentIndex = ref(0)
	let cardElements: HTMLElement[] = []
	const intersectionRatios = new Map<HTMLElement, number>()
	let observer: IntersectionObserver | null = null

	// Update the arrow visibility based on the container's scroll position.
	const updateArrows = () => {
		const el = carouselRef.value
		if (!el) return
		const { scrollLeft, clientWidth, scrollWidth } = el
		// If the container doesn't overflow, hide both arrows.
		if (scrollWidth <= clientWidth) {
			showLeftArrow.value = false
			showRightArrow.value = false
		} else {
			// Show left arrow if we've scrolled away from the very start.
			showLeftArrow.value = scrollLeft > 0
			// Show right arrow if there's more content to scroll to on the right.
			showRightArrow.value = scrollLeft + clientWidth < scrollWidth
		}
	}
	// Helper to build a list of thresholds for the Intersection Observer.
	const buildThresholdList = (): number[] => {
		const thresholds: number[] = []
		for (let i = 0; i <= 1; i += 0.1) {
			thresholds.push(parseFloat(i.toFixed(1)))
		}
		return thresholds
	}
	const updateCurrentIndex = () => {
		let maxRatio = 0
		let maxElement: HTMLElement | null = null
		cardElements.forEach(card => {
			const ratio = intersectionRatios.get(card) || 0
			if (ratio > maxRatio) {
				maxRatio = ratio
				maxElement = card
			}
		})
		if (maxElement) {
			const index = cardElements.indexOf(maxElement)
			currentIndex.value = index
		}
	}
	const observerCallback: IntersectionObserverCallback = entries => {
		entries.forEach(entry => {
			intersectionRatios.set(entry.target as HTMLElement, entry.intersectionRatio)
		})
		updateCurrentIndex()
	}
	// Scroll left by the container's visible width with smooth animation.
	const scrollLeftAction = () => {
		const el = carouselRef.value
		if (!el) return
		el.scrollBy({
			left: -el.clientWidth,
			behavior: 'smooth'
		})
	}

	// Scroll right by the container's visible width with smooth animation.
	const scrollRightAction = () => {
		const el = carouselRef.value
		if (!el) return
		el.scrollBy({
			left: el.clientWidth,
			behavior: 'smooth'
		})
	}

	const paste = useDebounceFn(() => {
		const el = carouselRef.value

		if (!el) return
		updateArrows()
		el.addEventListener('scroll', updateArrows)
		window.addEventListener('resize', updateArrows)

		// Query for the card elements using the provided selector.
		const cards = el.querySelectorAll(cardSelector)
		cardElements = Array.from(cards) as HTMLElement[]

		// Set up the Intersection Observer if cards are found.
		if (cardElements.length > 0) {
			observer = new IntersectionObserver(observerCallback, {
				root: el,
				threshold: buildThresholdList()
			})
			cardElements.forEach(card => observer?.observe(card))
		}
	}, 100)

	// Set up event listeners once the component is mounted.
	onMounted(paste)

	// Clean up event listeners when the component unmounts.
	onUnmounted(() => {
		const el = carouselRef.value
		if (el) {
			el.removeEventListener('scroll', updateArrows)
		}
		window.removeEventListener('resize', updateArrows)
		if (observer) {
			cardElements.forEach(card => observer?.unobserve(card))
			observer.disconnect()
		}
	})

	return {
		showLeftArrow,
		showRightArrow,
		scrollLeftAction,
		scrollRightAction,
		updateArrows,
		currentIndex
	}
}
